/**
 * Подключение и отключение Telegram-аккаунтов (§26 ТЗ).
 *
 * Эндпоинты существовали с самого начала, а экрана не было: аккаунты
 * предполагалось подключать через curl. Для администратора это неприемлемо —
 * без этой панели он не может завести ни одного аккаунта самостоятельно.
 *
 * Компонент ведёт машину состояний авторизации, а не изображает её. Стадию
 * назначает сервер полем `auth.stage`, клиент лишь показывает нужный шаг.
 * Придумывать переходы на клиенте нельзя: TDLib может запросить пароль второго
 * фактора и после кода, и вместо него, и порядок знает только он.
 *
 * Ни код подтверждения, ни пароль второго фактора здесь не сохраняются: поля
 * очищаются сразу после отправки, в состояние компонента ничего не оседает.
 * Сами значения уходят на backend и живут только внутри Telegram Service —
 * это инвариант I1.
 */

import { useEffect, useState, type FormEvent } from "react";
import QRCode from "qrcode";
import type { AnyTelegramAccountDto } from "@tw/shared-types";
import { ApiError, type AuthStateDto, type CodeChannel } from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { accountLabel } from "./accountLabel.js";
import { AlertIcon, LockIcon, PencilIcon, TrashIcon } from "./icons.tsx";
import {
  ADMIN_HINT,
  ADMIN_PANEL,
  ADMIN_PANEL_TITLE,
  ADMIN_SUBTITLE,
} from "./adminPanel.js";
import { cn } from "@/lib/utils";
import { StateBadge } from "./StateBadge.tsx";

interface ConnectAccountPanelProps {
  accounts: AnyTelegramAccountDto[];
  onChanged: () => void;
  /**
   * Панель внутри чужой рамки — в модальном окне.
   *
   * Тогда собственные заголовок и рамка не рисуются: у окна есть свои, и
   * второй заголовок подряд читается как ошибка вёрстки.
   */
  embedded?: boolean;
}

export function ConnectAccountPanel({
  accounts,
  onChanged,
  embedded = false,
}: ConnectAccountPanelProps) {
  const { api, can } = useSession();

  const [alias, setAlias] = useState("");
  const [phone, setPhone] = useState("");
  const [method, setMethod] = useState<"phone" | "qr">("phone");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");

  const [pending, setPending] = useState<{
    id: string;
    auth: AuthStateDto;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * QR-код входа, отрисованный в картинку.
   *
   * Рисуем на клиенте, а не на сервере: код живёт секунды и обновляется сам,
   * гонять его через API значило бы добавить круг задержки там, где важна
   * каждая секунда — просроченный код Telegram не примет.
   */
  const [qrImage, setQrImage] = useState<string | null>(null);

  /*
   * Обратный отсчёт до повторной отправки.
   *
   * Telegram называет срок (`code_info.timeout`): раньше него повтор
   * отклоняется — так ведут себя и официальные клиенты («SMS через 1:59»).
   * Кнопка без отсчёта давала нажать сразу и получала отказ, который
   * выглядел как «SMS не работает».
   */
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timeout = pending?.auth.codeTimeoutSeconds;
    setResendAt(pending?.auth.stage === "WAIT_CODE" && timeout ? Date.now() + timeout * 1000 : null);
  }, [pending]);
  useEffect(() => {
    if (resendAt === null) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [resendAt]);
  const resendWait = resendAt !== null ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;

  /*
   * Ожидание подтверждения QR.
   *
   * ── Что здесь чинится ─────────────────────────────────────────────────
   *
   * У входа по коду каждый шаг — запрос пользователя, и форма продвигается
   * ответом на него. У QR такого шага нет: сотрудник сканирует код своим
   * Telegram, и переход к «готово» (или к вводу облачного пароля) происходит
   * на сервере сам, отдельным апдейтом TDLib. Панель его не слушала —
   * поэтому QR сканировался, вход на сервере проходил, а окно так и висело
   * на картинке. Снаружи: «код сканируется, но вход не происходит».
   *
   * Пока показан QR, опрашиваем состояние раз в 2 секунды. Это же подхватывает
   * и обновление самой ссылки: TDLib периодически выдаёт новый токен, и QR на
   * экране обновляется, не протухая.
   */
  useEffect(() => {
    if (pending?.auth.stage !== "WAIT_QR_SCAN") return;
    const accountId = pending.id;
    let cancelled = false;
    let inFlight = false;

    const poll = async (): Promise<void> => {
      if (inFlight) return;
      inFlight = true;
      try {
        const { auth } = await api.accountAuthState(accountId);
        if (cancelled) return;
        if (auth.stage !== "WAIT_QR_SCAN") {
          // Вход подтверждён (или требуется пароль/код) — уводим форму дальше.
          advance(accountId, auth);
        } else if (auth.qrLoginUrl && auth.qrLoginUrl !== pending.auth.qrLoginUrl) {
          // Токен обновился — перерисуем свежий QR.
          setPending({ id: accountId, auth });
        }
      } catch {
        // Разовый сбой опроса не должен ронять экран: следующий тик повторит.
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(() => void poll(), 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // advance стабильна по поведению; включать её в зависимости — перезапускать
    // опрос на каждый рендер.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, api]);

  useEffect(() => {
    const url = pending?.auth.qrLoginUrl;
    if (!url) {
      setQrImage(null);
      return;
    }

    let cancelled = false;

    void QRCode.toDataURL(url, {
      // Запас на исправление ошибок: код смотрят с телефона под углом и при
      // неудачном освещении, и «M» здесь заметно надёжнее минимального «L».
      errorCorrectionLevel: "M",
      margin: 1,
      width: 512,
    })
      .then((dataUrl) => {
        if (!cancelled) setQrImage(dataUrl);
      })
      .catch(() => {
        // Не нарисовался — под кодом остаётся ссылка, ею можно
        // воспользоваться на этом же компьютере.
        if (!cancelled) setQrImage(null);
      });

    return () => {
      cancelled = true;
    };
  }, [pending?.auth.qrLoginUrl]);

  const canConnect = can("telegram.account.connect");
  const canDisconnect = can("telegram.account.disconnect");
  const canUpdate = can("telegram.account.update");
  const canDelete = can("telegram.account.delete");

  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [newAlias, setNewAlias] = useState("");

  if (!canConnect && !canDisconnect && !canUpdate && !canDelete) return null;

  const fail = (err: unknown): void => {
    if (err instanceof ApiError) {
      setError(
        err.isForbidden ? "Недостаточно прав для этого действия" : err.message,
      );
    } else {
      setError(err instanceof Error ? err.message : "Неизвестная ошибка");
    }
  };

  /** Общий разбор ответа: сервер сам сообщает, какой шаг следующий. */
  const advance = (accountId: string, auth: AuthStateDto): void => {
    if (auth.stage === "READY") {
      setPending(null);
      setAlias("");
      setPhone("");
      setNotice("Аккаунт подключён");
      onChanged();
      return;
    }
    setPending({ id: accountId, auth });
  };

  const start = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { account, auth } = await api.connectAccount({
        method,
        ...(method === "phone" ? { phone: phone.trim() } : {}),
        ...(alias.trim() ? { alias: alias.trim() } : {}),
      });
      advance(account.id, auth);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const { auth } = await api.submitAuthCode(pending.id, code.trim());
      setCode("");
      advance(pending.id, auth);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Код ещё раз — другим способом.
   *
   * Telegram шлёт код в приложение только на устройство, где номер уже
   * вошёл; если такого нет, код идёт SMS-ом или звонком. Повтор переключает
   * канал (`nextCodeChannel`), и это единственный выход из «код не приходит».
   */
  const resend = async (): Promise<void> => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const { auth } = await api.resendAuthCode(pending.id);
      setPending({ id: pending.id, auth });
      setNotice(`Код отправлен ещё раз${auth.codeChannel ? `: ${describeChannel(auth.codeChannel, auth.emailPattern)}` : ""}`);
    } catch (err) {
      // Telegram не даёт другого способа для этого номера — так бывает с
      // номерами, у которых нет SMS-доставки, и при частых попытках.
      // Единственный выход тогда — QR.
      setError(
        "Telegram не отправил код другим способом. Подключите аккаунт по QR-коду — код при этом не нужен.",
      );
      void err;
    } finally {
      setBusy(false);
    }
  };

  /** Без кода вообще: QR сканируется тем Telegram, где номер уже вошёл. */
  const switchToQr = async (): Promise<void> => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { auth } = await api.switchAuthToQr(pending.id);
      setMethod("qr");
      advance(pending.id, auth);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const sendPassword = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const { auth } = await api.submitAuthPassword(pending.id, password);
      setPassword("");
      advance(pending.id, auth);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const rename = async (account: AnyTelegramAccountDto): Promise<void> => {
    const alias = newAlias.trim();
    if (!alias || alias === accountLabel(account)) {
      setRenaming(null);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.updateAccount(account.id, { alias });
      setRenaming(null);
      setNotice("Название изменено");
      onChanged();
    } catch (err) {
      // Сервер отвергает alias, совпадающий с именем, username или хвостом
      // номера: иначе маскирование для стажёра теряет смысл (§7 ТЗ).
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Удаление аккаунта. Подтверждение — вводом названия, а не кнопкой «ОК».
   *
   * Действие необратимо и отличается от отключения: пропадут назначения
   * сотрудников и привязка чатов. Ввод названия заставляет прочитать, что
   * именно удаляется, — с двумя похожими строками в списке промахнуться легко.
   *
   * Поле ввода в самой строке, а не window.prompt: в Electron prompt не
   * поддерживается вовсе и бросает исключение, из-за чего кнопка молча
   * переставала работать.
   */
  const remove = async (account: AnyTelegramAccountDto): Promise<void> => {
    const label = accountLabel(account);

    if (deleteConfirm.trim() !== label) {
      setError("Название не совпало — аккаунт не удалён");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount(account.id);
      setNotice(`${label} удалён`);
      setDeleting(null);
      setDeleteConfirm("");
      onChanged();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (account: AnyTelegramAccountDto): Promise<void> => {
    // Отключение рвёт сессию TDLib. Восстановить её можно только повторной
    // авторизацией с кодом из Telegram, то есть с участием владельца номера.
    const confirmed = window.confirm(
      `Отключить ${accountLabel(account)}?\n\n` +
        "Сессия Telegram будет завершена. Повторное подключение потребует " +
        "кода подтверждения, который придёт владельцу номера.",
    );
    if (!confirmed) return;

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.disconnectAccount(account.id);
      setNotice(`${accountLabel(account)} отключён`);
      onChanged();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const stage = pending?.auth.stage;

  return (
    <section className={embedded ? "flex flex-col gap-3" : ADMIN_PANEL}>
      {!embedded && (
        <h2 className={ADMIN_PANEL_TITLE}>Подключение аккаунтов</h2>
      )}
      <p className={ADMIN_HINT}>
        Код подтверждения придёт в приложение Telegram указанного номера.
        Рабочие аккаунты подключаются только после проверки на запасном.
      </p>

      {error && (
        <p
          className="flex items-center gap-1.5 text-sm text-destructive"
          role="alert"
        >
          <AlertIcon /> {error}
        </p>
      )}
      {notice && <p className="text-sm text-[var(--ok)]">{notice}</p>}

      {canConnect && !pending && (
        <form
          className="mb-3 flex flex-col gap-3"
          onSubmit={(e) => void start(e)}
        >
          <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
            <span>Название</span>
            <input
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="Например, Продажи · Мария"
              maxLength={64}
            />
            {/* Alias виден стажёру вместо настоящего имени аккаунта, поэтому
                сервер отклонит совпадение с именем, username или хвостом
                номера. Пустое поле — сервер подставит «Account NN». */}
            <span className="text-[11.5px] font-normal leading-relaxed text-muted-foreground">
              Стажёр увидит именно это название. Оставьте пустым — сервер
              подставит обезличенное.
            </span>
          </label>

          <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
            <span>Способ входа</span>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as "phone" | "qr")}
            >
              <option value="phone">По номеру телефона</option>
              <option value="qr">По QR-коду</option>
            </select>
          </label>

          {method === "phone" && (
            <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
              <span>Номер телефона</span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+79991234567"
                inputMode="tel"
                required
              />
              <span className="text-[11.5px] font-normal leading-relaxed text-muted-foreground">
                Формат E.164. Код подтверждения придёт в приложение Telegram
                этого номера, а не по SMS.
              </span>
            </label>
          )}

          <button
            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-45"
            type="submit"
            disabled={busy}
          >
            {busy ? "Подключение…" : "Подключить"}
          </button>
        </form>
      )}

      {stage === "WAIT_CODE" && (
        <form
          className="mb-3 flex flex-col gap-3"
          onSubmit={(e) => void sendCode(e)}
        >
          {/*
            Куда ушёл код — словами. Раньше здесь всегда было «из приложения
            Telegram», хотя Telegram сам выбирает канал: в приложение — только
            на устройство, где номер уже вошёл, иначе SMS или звонок. Человек
            ждал не там.
          */}
          <p className="m-0 flex items-center gap-1.5 text-sm text-muted-foreground">
            {pending?.auth.codeChannel
              ? `Код отправлен ${describeChannel(pending.auth.codeChannel, pending.auth.emailPattern)}`
              : "Введите код из приложения Telegram"}
            {pending?.auth.codeLength ? ` · ${pending.auth.codeLength} цифр` : ""}
          </p>
          {pending?.auth.codeChannel === "telegramMessage" && (
            <p className="m-0 text-xs leading-relaxed text-muted-foreground">
              Код придёт в чат «Telegram» (синяя галочка) на устройстве, где этот номер
              уже вошёл в Telegram, — а не по SMS.
              {!pending?.auth.nextCodeChannel &&
                " Другого способа Telegram для этого номера не даёт: SMS и звонок недоступны."}{" "}
              Если кода там нет — подключите по QR-коду: его сканируют тем же Telegram
              (Настройки → Устройства → Подключить устройство), код вводить не нужно.
            </p>
          )}
          <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
            <span>Код подтверждения</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={16}
              required
              autoFocus
            />
          </label>
          <div className="flex gap-2">
            <button
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-45"
              type="submit"
              disabled={busy}
            >
              {busy ? "Проверка…" : "Подтвердить"}
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-brand px-4 py-2 text-sm font-medium text-brand transition-colors hover:bg-secondary disabled:opacity-45"
              onClick={() => void switchToQr()}
              disabled={busy}
            >
              Подключить по QR-коду
            </button>
            {/*
              Повтор предлагаем ТОЛЬКО когда Telegram назвал следующий способ
              (`nextCodeChannel`). Без него `resendAuthenticationCode`
              отвечает «code can't be resend» — кнопка была бы обманом:
              нажимаешь и получаешь ошибку. Для таких номеров Telegram даёт
              единственный канал — в приложение, и запасной путь только QR.
            */}
            {pending?.auth.codeChannel !== "email" && pending?.auth.nextCodeChannel && (
              <button
                type="button"
                onClick={() => void resend()}
                disabled={busy || resendWait > 0}
                title={`Следующий способ: ${describeChannel(pending.auth.nextCodeChannel)}`}
              >
                {`Отправить ${describeChannel(pending.auth.nextCodeChannel)}`}
                {resendWait > 0 ? ` (через ${formatWait(resendWait)})` : ""}
              </button>
            )}
            <button
              type="button"
              onClick={() => setPending(null)}
              disabled={busy}
            >
              Отменить
            </button>
          </div>
        </form>
      )}

      {stage === "WAIT_PASSWORD" && (
        <form
          className="mb-3 flex flex-col gap-3"
          onSubmit={(e) => void sendPassword(e)}
        >
          <p className="m-0 flex items-center gap-1.5 text-sm text-muted-foreground">
            <LockIcon /> На аккаунте включена двухэтапная проверка
          </p>
          <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
            <span>Пароль второго фактора</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
              required
              autoFocus
            />
            {pending?.auth.passwordHint && (
              <span className="text-[11.5px] font-normal leading-relaxed text-muted-foreground">
                Подсказка: {pending.auth.passwordHint}
              </span>
            )}
          </label>
          <div className="flex gap-2">
            <button
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-45"
              type="submit"
              disabled={busy}
            >
              {busy ? "Проверка…" : "Подтвердить"}
            </button>
            <button
              type="button"
              onClick={() => setPending(null)}
              disabled={busy}
            >
              Отменить
            </button>
          </div>
        </form>
      )}

      {/*
        Шаг начат, а состояние — снова «ждём телефон».

        Так бывает в двух случаях: апдейт TDLib пришёл позже ответа (тогда
        достаточно перечитать состояние), либо Telegram требует того, чего
        система не делает — регистрации номера или привязки почты для входа.
        Раньше здесь не рисовалось ничего: ни поля для кода, ни объяснения, и
        человек решал, что «код не приходит».
      */}
      {pending && stage === "WAIT_PHONE" && (
        <div className="mb-3 flex flex-col gap-2 text-sm text-muted-foreground">
          <p className="m-0">Telegram ещё не запросил код.</p>
          <p className="m-0 text-xs leading-relaxed">
            Обычно состояние обновляется за секунду — нажмите «Проверить». Если
            не меняется, Telegram требует регистрацию номера или привязку почты
            для входа: войдите этим номером в Telegram на телефоне, затем
            подключите его здесь заново.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-45"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                setError(null);
                void api
                  .accountAuthState(pending.id)
                  .then(({ auth }) => advance(pending.id, auth))
                  .catch(fail)
                  .finally(() => setBusy(false));
              }}
            >
              Проверить
            </button>
            <button type="button" onClick={() => setPending(null)} disabled={busy}>
              Отменить
            </button>
          </div>
        </div>
      )}

      {stage === "WAIT_QR_SCAN" && (
        <div className="mb-3 flex flex-col gap-3">
          <p className="m-0 flex items-center gap-1.5 text-sm text-muted-foreground">
            Откройте Telegram на телефоне: Настройки → Устройства → Подключить
            устройство — и наведите камеру на код.
          </p>

          {/*
            Код именно картинкой.

            Здесь стояла одна ссылка с комментарием, что генератор — лишняя
            зависимость, а ссылка содержит то же самое. Второе верно, первое
            нет: экран «Подключить устройство» в Telegram открывает КАМЕРУ,
            вставить туда текст нельзя. Без картинки подключение по QR не
            работало вовсе — оставался только вход по номеру.
          */}
          {qrImage ? (
            <img
              src={qrImage}
              alt="QR-код для входа в Telegram"
              className="mx-auto size-52 rounded-xl bg-white p-2"
            />
          ) : (
            <div className="mx-auto grid size-52 place-items-center rounded-xl bg-muted text-xs text-muted-foreground">
              Готовим код…
            </div>
          )}

          {/*
            Ссылка остаётся под кодом: на неё можно нажать, если Telegram
            открыт на этом же компьютере, и её видно при разборе, когда код
            почему-то не сканируется.
          */}
          <code className="break-all rounded-xl bg-muted p-2 font-mono text-[11px]">
            {pending?.auth.qrLoginUrl ?? "—"}
          </code>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPending(null)}>
              Отменить
            </button>
          </div>
        </div>
      )}

      {accounts.length > 0 && (
        <div className="mt-1">
          <h3 className={ADMIN_SUBTITLE}>Подключённые аккаунты</h3>
          {accounts.map((account) => (
            <div
              key={account.id}
              className="mt-1.5 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2"
            >
              {renaming === account.id ? (
                <>
                  <input
                    className="min-w-0 flex-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring"
                    value={newAlias}
                    onChange={(e) => setNewAlias(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void rename(account);
                      if (e.key === "Escape") setRenaming(null);
                    }}
                    maxLength={64}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="flex-none rounded-full border-transparent bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-45"
                    onClick={() => void rename(account)}
                    disabled={busy}
                  >
                    Сохранить
                  </button>
                  <button
                    type="button"
                    className="flex-none rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent disabled:opacity-45"
                    onClick={() => setRenaming(null)}
                    disabled={busy}
                  >
                    Отмена
                  </button>
                </>
              ) : (
                <>
                  {/* Точка состояния вместо голого кода: подключённый и
                      отвалившийся аккаунт должны различаться с одного взгляда,
                      а не по чтению строки CONNECTED против DISCONNECTED. */}
                  <span
                    aria-hidden
                    className={cn(
                      "status-dot",
                      account.connectionState === "CONNECTED"
                        ? "text-dot-done"
                        : "text-dot-progress",
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-heading">
                    {accountLabel(account)}
                  </span>
                  <StateBadge state={account.connectionState} />

                  {canUpdate && (
                    <button
                      type="button"
                      className="grid flex-none place-items-center rounded-lg px-2 py-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      aria-label={`Переименовать ${accountLabel(account)}`}
                      title="Переименовать"
                      onClick={() => {
                        setRenaming(account.id);
                        setNewAlias(accountLabel(account));
                      }}
                      disabled={busy}
                    >
                      <PencilIcon />
                    </button>
                  )}

                  {canDisconnect && (
                    <button
                      type="button"
                      onClick={() => void disconnect(account)}
                      disabled={busy}
                    >
                      Отключить
                    </button>
                  )}

                  {canDelete && (
                    <button
                      type="button"
                      className="grid flex-none place-items-center rounded-lg px-2 py-1.5 text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive"
                      aria-label={`Удалить ${accountLabel(account)}`}
                      title="Удалить из системы"
                      onClick={() => {
                        setDeleting(account.id);
                        setDeleteConfirm("");
                        setError(null);
                      }}
                      disabled={busy}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </>
              )}

              {deleting === account.id && (
                <div className="mt-1 flex w-full flex-col gap-1.5 rounded-xl bg-destructive/10 p-2.5">
                  <p className="text-xs leading-relaxed text-destructive">
                    Удаление необратимо и отличается от отключения: пропадут
                    назначения сотрудников и привязка чатов. В Telegram аккаунт
                    останется.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Введите{" "}
                    <strong className="text-foreground">
                      {accountLabel(account)}
                    </strong>{" "}
                    для подтверждения:
                  </p>
                  <div className="flex gap-2">
                    <input
                      autoFocus
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void remove(account);
                        if (e.key === "Escape") setDeleting(null);
                      }}
                      className="min-w-0 flex-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring"
                    />
                    <button
                      type="button"
                      className="flex-none rounded-lg bg-destructive px-3 py-1.5 text-sm text-white disabled:opacity-45"
                      disabled={
                        busy || deleteConfirm.trim() !== accountLabel(account)
                      }
                      onClick={() => void remove(account)}
                    >
                      Удалить
                    </button>
                    <button
                      type="button"
                      className="flex-none rounded-lg px-3 py-1.5 text-sm"
                      onClick={() => setDeleting(null)}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Канал доставки кода — словами, в творительном/предложном падеже после «отправлен». */
function describeChannel(channel: CodeChannel, emailPattern?: string): string {
  switch (channel) {
    case "telegramMessage":
      return "в приложение Telegram на другом устройстве";
    case "sms":
      return "по SMS";
    case "call":
      return "звонком — код продиктуют";
    case "flashCall":
      return "звонком — код это последние цифры входящего номера";
    case "missedCall":
      return "пропущенным звонком — код это последние цифры номера";
    case "fragment":
      return "на fragment.com (анонимный номер)";
    case "email":
      return emailPattern ? `на почту ${emailPattern}` : "на почту для входа";
    default:
      return "Telegram";
  }
}

function formatWait(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return m > 0 ? `${m}:${String(sec).padStart(2, "0")}` : `${sec} с`;
}
